import {
  Box,
  Grid,
  GridItem,
  Heading,
  VStack,
  Text,
} from '@chakra-ui/react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { Users, FileText, Folder, Tag, TrendingUp, FileCheck } from 'lucide-react'
import { useAuthStore } from '@/store/auth'
import { useEffect, useState } from 'react'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { StatCard, LineChart, PieChart } from '@/components/dashboard'
import {
  dashboardService,
  DashboardStats,
  EnhancedDashboardStats,
  ChartData,
} from '@/lib/services/dashboardService'

export default function DashboardPage() {
  const { user } = useAuthStore()
  const colors = useThemeColors()
  const [stats, setStats] = useState<DashboardStats>({
    total_users: 0,
    total_media: 0,
    total_categories: 0,
    total_tags: 0,
  })
  const [enhancedStats, setEnhancedStats] = useState<EnhancedDashboardStats | null>(null)
  const [userTrend, setUserTrend] = useState<ChartData | null>(null)
  const [pagesByStatus, setPagesByStatus] = useState<ChartData | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Fetch basic stats
        const basicStats = await dashboardService.getStats()
        setStats(basicStats)

        // Fetch enhanced stats
        // Temporarily disabled - API endpoint not available
        // try {
        //   const enhanced = await dashboardService.getEnhancedStats()
        //   setEnhancedStats(enhanced)
        // } catch (error) {
        //   console.warn('Enhanced stats not available:', error)
        // }

        // Fetch user registration trend
        // Temporarily disabled - API endpoint not available
        // try {
        //   const trend = await dashboardService.getUserRegistrationTrend('daily', 30)
        //   setUserTrend(trend)
        // } catch (error) {
        //   console.warn('User trend not available:', error)
        // }

        // Fetch pages by status
        // Temporarily disabled - API endpoint not available
        // try {
        //   const pagesStatus = await dashboardService.getPagesByStatus()
        //   setPagesByStatus(pagesStatus)
        // } catch (error) {
        //   console.warn('Pages by status not available:', error)
        // }
      } catch (error) {
        console.error('Error fetching dashboard data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchDashboardData()
  }, [])

  const statCards = [
    {
      title: 'Total Users',
      value: stats.total_users,
      icon: Users,
      color: 'blue',
    },
    {
      title: 'Media Files',
      value: stats.total_media,
      icon: FileText,
      color: 'green',
    },
    {
      title: 'Categories',
      value: stats.total_categories,
      icon: Folder,
      color: 'purple',
    },
    {
      title: 'Tags',
      value: stats.total_tags,
      icon: Tag,
      color: 'orange',
    },
  ]

  // Add enhanced stat cards if available
  if (enhancedStats) {
    statCards.push(
      {
        title: 'Active Users',
        value: enhancedStats.active_users,
        icon: Users,
        color: 'teal',
      },
      {
        title: 'Published Pages',
        value: enhancedStats.published_pages,
        icon: FileCheck,
        color: 'green',
      }
    )
  }

  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
        <VStack spacing={6} align="stretch" flex="1" overflowY="auto" minH={0}>
          <Box flexShrink={0}>
            <Heading size="lg" mb={2}>
              Welcome back, {user?.name || user?.user_login}!
            </Heading>
            <Text color={colors.text}>
              Here&apos;s what&apos;s happening with your admin panel today.
            </Text>
          </Box>

          {/* Stat Cards */}
          <Grid
            templateColumns={{
              base: '1fr',
              md: 'repeat(2, 1fr)',
              lg: 'repeat(3, 1fr)',
              xl: 'repeat(4, 1fr)',
            }}
            gap={6}
            flexShrink={0}
          >
            {statCards.map((stat, index) => (
              <GridItem key={index}>
                <StatCard
                  title={stat.title}
                  value={stat.value}
                  icon={stat.icon}
                  color={stat.color}
                  isLoading={isLoading}
                />
              </GridItem>
            ))}
          </Grid>

          {/* Charts Row */}
          <Grid
            templateColumns={{
              base: '1fr',
              md: 'repeat(2, 1fr)',
              lg: 'repeat(2, 1fr)',
            }}
            gap={6}
            flexShrink={0}
          >
            {/* User Registration Trend */}
            {userTrend && (
              <GridItem>
                <LineChart
                  title="User Registration Trend"
                  labels={userTrend.labels}
                  data={userTrend.data}
                  color="#3182CE"
                  isLoading={isLoading}
                />
              </GridItem>
            )}

            {/* Pages by Status */}
            {pagesByStatus && pagesByStatus.labels.length > 0 && (
              <GridItem>
                <PieChart
                  title="Pages by Status"
                  labels={pagesByStatus.labels}
                  data={pagesByStatus.data}
                  isLoading={isLoading}
                />
              </GridItem>
            )}
          </Grid>
        </VStack>
      </Box>
    </AdminLayout>
  )
}

